import React from 'react';
import { AppMode, User } from '../types';
import {
  Atom,
  MessageSquare,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  PenTool,
  ChevronLeft,
  ChevronRight,
  Settings,
  Map,
  GalleryVerticalEnd,
  Notebook,
  Compass,
  Target,
  ClipboardCheck,
  Lightbulb,
  Shield,
  Sparkles,
  ListChecks
} from 'lucide-react';
import { isAdminUser } from '../services/admin';

interface LayoutProps {
  children: React.ReactNode;
  currentMode: AppMode;
  setMode: (mode: AppMode) => void;
  user: User | null;
  onLogout: () => void;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  currentMode,
  setMode,
  user,
  onLogout,
  isSidebarCollapsed,
  toggleSidebar
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const moduleLabels: Record<AppMode, string> = {
    [AppMode.Landing]: 'Home',
    [AppMode.Auth]: 'Sign In',
    [AppMode.Dashboard]: 'My Progress',
    [AppMode.Notes]: 'Study Notes',
    [AppMode.Roadmap]: 'Revision Planner',
    [AppMode.Chat]: 'Talk To Atlas',
    [AppMode.QuickLearn]: 'Learn a Topic',
    [AppMode.ActiveRecall]: 'Practice Questions',
    [AppMode.MCQ]: 'Multiple Choice',
    [AppMode.Flashcards]: 'Flashcards',
    [AppMode.Focus]: 'Focus Studio',
    [AppMode.Solver]: 'Step-by-Step Solver',
    [AppMode.PaperGuide]: 'Past Paper Practice',
    [AppMode.InteractiveWhiteboard]: 'Working Board',
    [AppMode.AiMarker]: 'Mark My Work',
    [AppMode.Video]: 'Video Help',
    [AppMode.Voice]: 'Voice',
    [AppMode.Settings]: 'Settings',
    [AppMode.Admin]: 'Admin',
  };

  // Keyboard shortcuts (kept for power users, but not displayed in UI)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        switch (e.key) {
          case 'd':
            e.preventDefault();
            setMode(AppMode.Dashboard);
            break;
          case 'n':
            e.preventDefault();
            setMode(AppMode.Notes);
            break;
          case 'f':
            e.preventDefault();
            setMode(AppMode.Flashcards);
            break;
          case 'p':
            e.preventDefault();
            setMode(AppMode.ActiveRecall);
            break;
          case 't':
            e.preventDefault();
            setMode(AppMode.Chat);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setMode]);

  const NavItem = ({ mode, icon: Icon, label, dataTour }: { mode: AppMode, icon: any, label: string, dataTour?: string }) => {
    const isTabletPcOnly = [AppMode.InteractiveWhiteboard, AppMode.QuickLearn, AppMode.Notes].includes(mode);
    const isActive = currentMode === mode;

    return (
      <div className="w-full relative group">
        <button
          onClick={() => {
            setMode(mode);
            setIsMobileMenuOpen(false);
          }}
          data-tour={dataTour}
          className={`
            flex items-center w-full px-3 py-2.5 rounded-xl transition-all duration-200 relative
            ${isActive
              ? 'bg-gradient-to-r from-cobalt-600 to-cobalt-500 text-white shadow-lg shadow-cobalt-600/20'
              : 'text-slate-400 hover:bg-slate-800/60 hover:text-white'
            }
            ${isSidebarCollapsed ? 'justify-center px-2' : ''}
          `}
          title={isSidebarCollapsed ? label : undefined}
        >
          {/* Active indicator */}
          {isActive && !isSidebarCollapsed && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
          )}

          <Icon className={`w-5 h-5 flex-shrink-0 ${isSidebarCollapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : ''}`} />

          {(!isSidebarCollapsed || (typeof window !== 'undefined' && window.innerWidth < 768)) && (
            <div className="flex flex-col items-start overflow-hidden flex-1">
              <span className="text-sm font-medium truncate">{label}</span>
              {isTabletPcOnly && typeof window !== 'undefined' && window.innerWidth < 768 && (
                <span className="text-[9px] text-cobalt-300 font-semibold leading-tight">Best on Tablet/PC</span>
              )}
            </div>
          )}
        </button>

        {/* Tooltip for collapsed sidebar */}
        {isSidebarCollapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 whitespace-nowrap shadow-xl">
            {label}
          </div>
        )}
      </div>
    );
  };

  const SectionHeader = ({ title, icon: Icon }: { title: string, icon?: any }) => (
    !isSidebarCollapsed ? (
      <div className="px-3 pt-4 pb-2 flex items-center">
        {Icon && <Icon className="w-3 h-3 mr-2 text-slate-600" />}
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">{title}</span>
      </div>
    ) : <div className="my-3 mx-3 border-t border-slate-800/50" />
  );

  if (!user) {
    return (
      <div className="h-[100dvh] w-full overflow-y-auto bg-transparent">
        {children}
      </div>
    );
  }

  const safeName = (user.name || user.email || 'Student').trim() || 'Student';
  const userInitial = safeName.charAt(0).toUpperCase();
  const showAdmin = isAdminUser(user);

  return (
    <div className="flex h-[100dvh] bg-slate-950 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        data-tour="sidebar"
        className={`
          hidden md:flex flex-col
          bg-slate-900/95 backdrop-blur-xl
          border-r border-slate-800/50
          transition-all duration-300 ease-out
          ${isSidebarCollapsed ? 'w-[72px]' : 'w-64'}
        `}
      >
        {/* Logo & Brand */}
        <div className={`p-4 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} border-b border-slate-800/50`}>
          <div className="flex items-center space-x-2.5">
            <div className="relative">
              <div className="bg-gradient-to-br from-cobalt-500 to-cobalt-600 p-2 rounded-xl flex-shrink-0 shadow-lg shadow-cobalt-600/20">
                <Atom className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-slate-900" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-white tracking-tight">Absorbit</span>
                <span className="text-[9px] uppercase tracking-[0.2em] text-slate-500 -mt-0.5">Study Studio</span>
              </div>
            )}
          </div>
          {!isSidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {isSidebarCollapsed && (
          <div className="flex justify-center py-3">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2.5 py-2 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
          <NavItem mode={AppMode.Dashboard} icon={LayoutDashboard} label={moduleLabels[AppMode.Dashboard]} dataTour="progress" />

          <SectionHeader title="Plan" />
          <NavItem mode={AppMode.Notes} icon={Notebook} label={moduleLabels[AppMode.Notes]} dataTour="notes" />
          <NavItem mode={AppMode.Roadmap} icon={Map} label={moduleLabels[AppMode.Roadmap]} />

          <SectionHeader title="Learn" />
          <NavItem mode={AppMode.QuickLearn} icon={Lightbulb} label={moduleLabels[AppMode.QuickLearn]} dataTour="learn" />
          <NavItem mode={AppMode.Chat} icon={MessageSquare} label={moduleLabels[AppMode.Chat]} />
          <NavItem mode={AppMode.Flashcards} icon={GalleryVerticalEnd} label={moduleLabels[AppMode.Flashcards]} />

          <SectionHeader title="Practice" />
          <NavItem mode={AppMode.ActiveRecall} icon={Target} label={moduleLabels[AppMode.ActiveRecall]} dataTour="practice" />
          <NavItem mode={AppMode.MCQ} icon={ListChecks} label={moduleLabels[AppMode.MCQ]} />
          <NavItem mode={AppMode.InteractiveWhiteboard} icon={PenTool} label={moduleLabels[AppMode.InteractiveWhiteboard]} dataTour="whiteboard" />

          <SectionHeader title="Review" />
          <NavItem mode={AppMode.PaperGuide} icon={Compass} label={moduleLabels[AppMode.PaperGuide]} />
          <NavItem mode={AppMode.AiMarker} icon={ClipboardCheck} label={moduleLabels[AppMode.AiMarker]} />

          {showAdmin && (
            <>
              <SectionHeader title="Admin" />
              <NavItem mode={AppMode.Admin} icon={Shield} label={moduleLabels[AppMode.Admin]} />
            </>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t border-slate-800/50 space-y-2">
          <NavItem mode={AppMode.Settings} icon={Settings} label="Settings" />

          {/* User Profile */}
          {!isSidebarCollapsed && (
            <div className="mt-2 px-2 py-2.5 flex items-center bg-slate-800/30 rounded-xl border border-slate-800/50">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cobalt-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-lg">
                {userInitial}
              </div>
              <div className="ml-2.5 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{safeName}</p>
                <p className="text-[10px] text-slate-500 flex items-center">
                  <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                  Student
                </p>
              </div>
              <button
                onClick={onLogout}
                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}

          {isSidebarCollapsed && (
            <div className="flex flex-col items-center space-y-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cobalt-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white shadow-lg">
                {userInitial}
              </div>
              <button
                onClick={onLogout}
                className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative h-full w-[280px] bg-slate-900 flex flex-col animate-in slide-in-from-left duration-300">
            {/* Mobile Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-800">
              <div className="flex items-center space-x-2.5">
                <div className="bg-gradient-to-br from-cobalt-500 to-cobalt-600 p-2 rounded-xl">
                  <Atom className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">Absorbit</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 p-3 overflow-y-auto space-y-1">
              <NavItem mode={AppMode.Dashboard} icon={LayoutDashboard} label={moduleLabels[AppMode.Dashboard]} />

              <div className="pt-4 pb-2 px-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">Plan</span>
              </div>
              <NavItem mode={AppMode.Notes} icon={Notebook} label={moduleLabels[AppMode.Notes]} />
              <NavItem mode={AppMode.Roadmap} icon={Map} label={moduleLabels[AppMode.Roadmap]} />

              <div className="pt-4 pb-2 px-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">Learn</span>
              </div>
              <NavItem mode={AppMode.QuickLearn} icon={Lightbulb} label={moduleLabels[AppMode.QuickLearn]} />
              <NavItem mode={AppMode.Chat} icon={MessageSquare} label={moduleLabels[AppMode.Chat]} />
              <NavItem mode={AppMode.Flashcards} icon={GalleryVerticalEnd} label={moduleLabels[AppMode.Flashcards]} />

              <div className="pt-4 pb-2 px-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">Practice</span>
              </div>
              <NavItem mode={AppMode.ActiveRecall} icon={Target} label={moduleLabels[AppMode.ActiveRecall]} />
              <NavItem mode={AppMode.MCQ} icon={ListChecks} label={moduleLabels[AppMode.MCQ]} />
              <NavItem mode={AppMode.InteractiveWhiteboard} icon={PenTool} label={moduleLabels[AppMode.InteractiveWhiteboard]} />

              <div className="pt-4 pb-2 px-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">Review</span>
              </div>
              <NavItem mode={AppMode.PaperGuide} icon={Compass} label={moduleLabels[AppMode.PaperGuide]} />
              <NavItem mode={AppMode.AiMarker} icon={ClipboardCheck} label={moduleLabels[AppMode.AiMarker]} />

              {showAdmin && (
                <>
                  <div className="pt-4 pb-2 px-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">Admin</span>
                  </div>
                  <NavItem mode={AppMode.Admin} icon={Shield} label={moduleLabels[AppMode.Admin]} />
                </>
              )}

              <div className="pt-4 border-t border-slate-800 mt-4">
                <NavItem mode={AppMode.Settings} icon={Settings} label="Settings" />
              </div>
            </nav>

            {/* Mobile User Section */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex items-center bg-slate-800/30 rounded-xl p-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cobalt-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white">
                  {userInitial}
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-white">{safeName}</p>
                  <p className="text-xs text-slate-500">Student</p>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="w-full mt-3 flex items-center justify-center px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative bg-slate-950">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-3 bg-slate-900/95 backdrop-blur border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="bg-gradient-to-br from-cobalt-500 to-cobalt-600 p-1.5 rounded-lg">
              <Atom className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-bold text-white">Absorbit</span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
          >
            <Menu className="w-5 h-5" />
          </button>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto relative w-full h-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
